import { LoadingButton } from '@mui/lab'
import { alpha } from '@mui/material/styles'
import styled from 'styled-components'
import { theme } from 'theme'

// All colors come from the Nutrisense MUI theme (src/theme.ts). The root element
// re-exposes the values it needs as CSS variables so the styled primitives below can
// reference them tersely.
const palette = theme.palette
const ink = palette['text-color'].primary
const surface = palette['theme-primary']['level-1']

export const OnboardingRootStyled = styled.div`
  --brand-primary: ${palette['brand-primary'].main};
  --brand-primary-dark: ${palette['brand-primary'].dark};
  --brand-primary-08: ${palette['brand-primary-transparent']['transparent-8']};
  --brand-primary-16: ${palette['brand-primary-transparent']['transparent-16']};
  --brand-primary-24: ${palette['brand-primary-transparent']['transparent-24']};

  --cream-base: ${palette['brand-secondary'].base};
  --cream-dark: ${palette['brand-secondary'].dark};
  --cream-darker: ${palette['brand-secondary'].darker};

  --ink: ${ink};
  --muted: ${palette['text-color'].secondary};
  --muted-2: ${palette['text-color'].newSecondary};
  --placeholder: ${palette.icon.secondary};

  --surface: ${surface};
  --surface-muted: ${palette['theme-primary']['level-2']};
  --overlay-hover: ${alpha(ink, 0.05)};

  --border-2: ${palette['theme-alternative-transparent']['transparent-8']};
  --border-3: ${palette['theme-alternative-transparent']['transparent-16']};

  --danger: ${palette.danger.main};
  --danger-08: ${alpha(palette.danger.main, 0.08)};
  --danger-16: ${alpha(palette.danger.main, 0.16)};

  --topbar-bg: ${alpha(palette['brand-secondary'].base, 0.85)};
  --actionbar-bg: ${alpha(surface, 0.92)};
  --shadow-card: 0 1px 0 ${alpha(ink, 0.02)};
  --shadow-bar: 0 -1px 0 ${palette['theme-alternative-transparent']['transparent-8']},
    0 -8px 20px ${alpha(ink, 0.04)};

  --radius-sm: 4px;
  --radius-md: 8px;
  --radius-lg: 12px;

  --font-serif: 'Instrument Serif', Georgia, serif;

  height: 100vh;
  overflow-y: auto;
  background: var(--cream-base);
  color: var(--ink);
  font-size: 15px;
  line-height: 1.5;
  -webkit-font-smoothing: antialiased;

  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }
`

/* ---------- Top bar ---------- */
export const TopBarStyled = styled.header`
  position: sticky;
  top: 0;
  z-index: 30;
  background: var(--topbar-bg);
  backdrop-filter: saturate(140%) blur(10px);
  border-bottom: 1px solid var(--border-2);
`
export const TopBarInnerStyled = styled.div`
  max-width: 1080px;
  margin: 0 auto;
  padding: 14px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`
export const BrandStyled = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  color: var(--ink);
  img {
    display: block;
    height: 22px;
    width: auto;
  }
  .pipe {
    width: 1px;
    height: 18px;
    background: var(--border-3);
    flex-shrink: 0;
  }
  .crumb {
    font-size: 13px;
    color: var(--muted-2);
    font-weight: 500;
    white-space: nowrap;
  }
  /* Drop the breadcrumb on very narrow screens so the bar fits (e.g. 360px). */
  @media (max-width: 480px) {
    gap: 8px;
    .pipe,
    .crumb {
      display: none;
    }
  }
`
export const TopBarRightStyled = styled.div`
  display: flex;
  align-items: center;
  gap: 18px;
  .user-chip {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    font-size: 13px;
    color: var(--muted-2);
  }
  .avatar {
    width: 28px;
    height: 28px;
    border-radius: 999px;
    background: var(--cream-darker);
    color: var(--ink);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    font-size: 12px;
  }
  @media (max-width: 640px) {
    .user-chip span:last-child {
      display: none;
    }
  }
`

/* ---------- Page ---------- */
export const PageStyled = styled.main`
  max-width: 640px;
  margin: 0 auto;
  padding: 40px 24px 140px;
  @media (max-width: 640px) {
    padding: 24px 16px 160px;
  }
`

/* ---------- Hero ---------- */
export const HeroStyled = styled.section`
  margin-bottom: 28px;
  .eyebrow {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--brand-primary-dark);
    background: var(--brand-primary-08);
    padding: 6px 10px;
    border-radius: 999px;
    margin-bottom: 16px;
  }
  .eyebrow .dot {
    width: 6px;
    height: 6px;
    border-radius: 999px;
    background: var(--brand-primary);
  }
  h1 {
    font-family: var(--font-serif);
    font-weight: 400;
    font-size: 38px;
    line-height: 1.1;
    letter-spacing: -0.01em;
    margin: 0 0 10px;
    color: var(--ink);
  }
  h1 em {
    font-style: italic;
    color: var(--muted-2);
  }
  p {
    margin: 0;
    color: var(--muted-2);
    font-size: 15.5px;
    max-width: 56ch;
  }
  @media (max-width: 640px) {
    h1 {
      font-size: 30px;
    }
  }
`
export const LegendStyled = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  font-size: 12.5px;
  color: var(--muted);
  margin-top: 18px;
  .req {
    color: var(--danger);
    font-weight: 600;
  }
`
export const ProgressStyled = styled.div`
  position: sticky;
  top: 58px;
  z-index: 20;
  margin-top: 22px;
  padding: 14px 0;
  background: var(--cream-base);
  border-bottom: 1px solid var(--border-2);
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: 1fr;
  gap: 8px;
  .step {
    appearance: none;
    background: transparent;
    border: 0;
    padding: 4px 0;
    margin: 0;
    text-align: left;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    gap: 6px;
    font-family: inherit;
    font-size: 12px;
    color: var(--muted);
    font-weight: 500;
    transition: color 120ms ease;
  }
  .step:hover,
  .step:focus-visible {
    color: var(--ink);
  }
  .step:focus-visible {
    outline: 2px solid var(--brand-primary);
    outline-offset: 2px;
    border-radius: 4px;
  }
  .step.active {
    color: var(--ink);
  }
  .rail {
    height: 3px;
    background: var(--cream-dark);
    border-radius: 999px;
    overflow: hidden;
  }
  .rail span {
    display: block;
    height: 100%;
    background: var(--brand-primary);
    width: 0%;
    transition: width 320ms ease;
  }
  .step.done .rail span,
  .step.active .rail span {
    width: 100%;
  }
  @media (max-width: 600px) {
    display: none;
  }
`

/* ---------- Section card ---------- */
export const SectionStyled = styled.section`
  background: var(--surface);
  border: 1px solid var(--border-2);
  border-radius: var(--radius-lg);
  padding: 28px;
  margin-bottom: 20px;
  box-shadow: var(--shadow-card);
  scroll-margin-top: 90px;
  .section-head {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 22px;
    padding-bottom: 18px;
    border-bottom: 1px dashed var(--border-2);
  }
  .heading-block {
    display: flex;
    align-items: baseline;
    gap: 14px;
  }
  .num {
    font-family: var(--font-serif);
    font-style: italic;
    font-size: 28px;
    color: var(--brand-primary-dark);
    line-height: 1;
  }
  h2 {
    font-size: 17px;
    font-weight: 600;
    margin: 0;
    color: var(--ink);
  }
  .blurb {
    color: var(--muted-2);
    font-size: 13.5px;
  }
  .count {
    font-size: 12px;
    color: var(--muted);
    background: var(--cream-base);
    border: 1px solid var(--border-2);
    padding: 4px 10px;
    border-radius: 999px;
    white-space: nowrap;
  }
  .count.warn {
    color: var(--danger);
    border-color: var(--danger);
  }
  @media (max-width: 640px) {
    padding: 20px;
    .section-head {
      flex-direction: column;
      align-items: flex-start;
      gap: 8px;
    }
    .heading-block {
      flex-direction: column;
      gap: 4px;
    }
  }
`

/* ---------- Fields ---------- */
export const FieldStyled = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  & + & {
    margin-top: 18px;
  }
  .label {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    font-weight: 500;
    color: var(--ink);
  }
  .label .req {
    color: var(--danger);
  }
  .label .opt {
    font-size: 11px;
    color: var(--muted);
    background: var(--cream-base);
    border: 1px solid var(--border-2);
    padding: 1px 6px;
    border-radius: 4px;
    font-weight: 500;
    text-transform: uppercase;
  }
  .help {
    font-size: 12.5px;
    color: var(--muted);
    margin: -2px 0 2px;
  }
  .err {
    margin-top: 4px;
    font-size: 12.5px;
    color: var(--danger);
    display: flex;
    align-items: center;
    gap: 6px;
  }
  .counter {
    font-size: 12px;
    color: var(--muted);
    text-align: right;
    margin-top: 4px;
  }
  .counter.warn {
    color: var(--danger);
  }
`
export const RowStyled = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
  align-items: start;
  & + &,
  & + ${FieldStyled}, ${FieldStyled} + & {
    margin-top: 18px;
  }
  & > ${FieldStyled} + ${FieldStyled} {
    margin-top: 0;
  }
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
    gap: 0;
    & > ${FieldStyled} + ${FieldStyled} {
      margin-top: 18px;
    }
  }
`

// Plain bordered controls matching the design: 8px radius, 1px border, green focus ring,
// danger tint. Colors come from the theme via the CSS variables above.
const controlCss = `
  width: 100%;
  font: inherit;
  font-size: 14.5px;
  color: var(--ink);
  background: var(--surface);
  border: 1px solid var(--border-3);
  border-radius: var(--radius-md);
  padding: 11px 13px;
  transition: border-color 120ms ease, box-shadow 120ms ease, background 120ms ease;
  outline: none;
  &::placeholder { color: var(--placeholder); }
  &:hover { border-color: var(--muted); }
  &:focus {
    border-color: var(--brand-primary-dark);
    box-shadow: 0 0 0 3px var(--brand-primary-16);
  }
  &[aria-invalid='true'] {
    border-color: var(--danger);
    background: var(--danger-08);
  }
  &[aria-invalid='true']:focus {
    box-shadow: 0 0 0 3px var(--danger-16);
  }
`

export const InputStyled = styled.input`
  ${controlCss}
`
export const TextareaStyled = styled.textarea`
  ${controlCss}
  resize: vertical;
  min-height: 110px;
  line-height: 1.55;
`
export const SelectWrapStyled = styled.div`
  position: relative;
`
export const SelectStyled = styled.select`
  ${controlCss}
  appearance: none;
  padding-right: 36px;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'><path d='M2 4 L6 8 L10 4' fill='none' stroke='%23416461' stroke-width='1.6' stroke-linecap='round' stroke-linejoin='round'/></svg>");
  background-repeat: no-repeat;
  background-position: right 12px center;
  background-size: 12px 12px;
  &.is-placeholder {
    color: var(--placeholder);
  }
`
export const AffixWrapStyled = styled.div`
  position: relative;
  .affix {
    position: absolute;
    left: 13px;
    top: 50%;
    transform: translateY(-50%);
    color: var(--muted);
    font-size: 14px;
    pointer-events: none;
  }
  ${InputStyled} {
    padding-left: 116px;
  }
`

/* ---------- File dropzone ---------- */
export const DropzoneStyled = styled.label`
  border: 1.5px dashed var(--border-3);
  border-radius: var(--radius-md);
  background: var(--cream-base);
  padding: 16px;
  display: flex;
  align-items: center;
  gap: 14px;
  cursor: pointer;
  transition: border-color 120ms ease, background 120ms ease;
  &:hover,
  &.is-drag {
    border-color: var(--brand-primary-dark);
    background: var(--brand-primary-08);
  }
  &[aria-invalid='true'] {
    border-color: var(--danger);
    background: var(--danger-08);
  }
  /* When the form is rendered in submitted/read-only mode (FormFieldsetStyled is
     disabled), the <label> can't reach a :disabled pseudo, so we have to opt out
     of the hover affordance ourselves. */
  fieldset:disabled & {
    cursor: not-allowed;
  }
  fieldset:disabled &:hover {
    border-color: var(--border-3);
    background: var(--cream-base);
  }
  .ico {
    width: 40px;
    height: 40px;
    border-radius: 8px;
    background: var(--surface);
    border: 1px solid var(--border-2);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: var(--brand-primary-dark);
    flex-shrink: 0;
  }
  .copy {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }
  .t1 {
    font-size: 14px;
    font-weight: 500;
    color: var(--ink);
  }
  .t1 b {
    color: var(--brand-primary-dark);
  }
  .t2 {
    font-size: 12.5px;
    color: var(--muted);
  }
  input[type='file'] {
    display: none;
  }
`
export const FilePillStyled = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  margin-top: 10px;
  background: var(--brand-primary-08);
  border: 1px solid var(--brand-primary-24);
  color: var(--ink);
  padding: 8px 10px 8px 12px;
  border-radius: var(--radius-md);
  font-size: 13px;
  .name {
    font-weight: 500;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 320px;
  }
  .size {
    color: var(--muted-2);
    font-size: 12px;
  }
  .x {
    margin-left: auto;
    background: none;
    border: 0;
    cursor: pointer;
    color: var(--muted-2);
    padding: 4px;
    line-height: 0;
    border-radius: 4px;
    display: inline-flex;
  }
  .x:hover {
    background: var(--overlay-hover);
    color: var(--ink);
  }
`

/* ---------- Chip picker ---------- */
export const ChipGridStyled = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`
export const ChipStyled = styled.button`
  appearance: none;
  font: inherit;
  cursor: pointer;
  background: var(--surface);
  color: var(--ink);
  border: 1px solid var(--border-3);
  border-radius: var(--radius-sm);
  padding: 8px 12px;
  font-size: 13.5px;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: background 120ms ease, border-color 120ms ease;
  &:hover {
    border-color: var(--brand-primary-dark);
    background: var(--brand-primary-08);
  }
  &.is-selected {
    background: var(--brand-primary-08);
    border-color: var(--brand-primary-dark);
    color: var(--brand-primary-dark);
    font-weight: 600;
  }
  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
    background: var(--surface-muted);
  }
  &:disabled:hover {
    border-color: var(--border-3);
    background: var(--surface-muted);
  }
  .tick {
    width: 14px;
    height: 14px;
  }
`

/* ---------- Error summary ---------- */
export const ErrorSummaryStyled = styled.div`
  margin-bottom: 16px;
  background: var(--danger-08);
  border: 1px solid var(--danger);
  border-radius: var(--radius-md);
  padding: 14px 16px;
  color: var(--ink);
  font-size: 13.5px;
  strong {
    color: var(--danger);
  }
  ul {
    margin: 8px 0 0;
    padding-left: 18px;
    color: var(--muted-2);
  }
  li {
    margin: 2px 0;
  }
  a {
    color: var(--muted-2);
    text-underline-offset: 2px;
  }
  a:hover {
    color: var(--ink);
  }
`

/* ---------- Sticky action bar ---------- */
export const ActionBarStyled = styled.div`
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 25;
  background: var(--actionbar-bg);
  backdrop-filter: saturate(140%) blur(12px);
  box-shadow: var(--shadow-bar);
`
export const ActionBarInnerStyled = styled.div`
  max-width: 640px;
  margin: 0 auto;
  padding: 14px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  .save-meta {
    font-size: 12.5px;
    color: var(--muted);
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .save-meta .dot {
    width: 6px;
    height: 6px;
    border-radius: 999px;
    background: var(--brand-primary);
  }
  .actions {
    display: flex;
    align-items: center;
    gap: 10px;
  }
  @media (max-width: 640px) {
    padding: 12px 16px;
    flex-wrap: wrap;
    .save-meta {
      order: 2;
      width: 100%;
    }
    .actions {
      width: 100%;
    }
    .actions > * {
      flex: 1;
    }
  }
`

export const PageFootStyled = styled.p`
  text-align: center;
  color: var(--muted);
  font-size: 12.5px;
  margin: 8px 0 0;
`

/* ---------- Action bar buttons ---------- */
const actionButtonBase = `
  && {
    border-radius: 8px;
    text-transform: none;
    font-weight: 600;
    padding-left: 18px;
    padding-right: 18px;
  }
`

export const DraftButtonStyled = styled(LoadingButton)`
  ${actionButtonBase}
  && {
    color: var(--ink);
    background-color: var(--surface);
    border-color: var(--border-3);
  }
  &&:hover {
    background-color: var(--cream-base);
    border-color: var(--muted-2);
  }
`

export const SubmitButtonStyled = styled(LoadingButton)`
  ${actionButtonBase}
  && {
    color: #fff;
  }
`

/* ---------- Pending-register note ---------- */
export const PendingNoteStyled = styled.div`
  background: var(--brand-primary-08);
  border: 1px solid var(--brand-primary-dark);
  border-radius: var(--radius-md);
  padding: 12px 14px;
  margin: 16px 0 20px;
  .title {
    color: var(--brand-primary-dark);
    font-weight: 600;
  }
  .body {
    margin-top: 6px;
    color: var(--muted-2);
  }
`

/* ---------- Existing-file link ---------- */
export const ExistingFileLinkStyled = styled.a`
  color: var(--brand-primary-dark);
  text-decoration: underline;
`

/* ---------- Form fieldset (used to disable everything once submitted) ---------- */
export const FormFieldsetStyled = styled.fieldset`
  border: 0;
  padding: 0;
  margin: 0;
  min-width: 0;
  &[disabled] {
    cursor: not-allowed;
  }
  &[disabled] input,
  &[disabled] select,
  &[disabled] textarea,
  &[disabled] button {
    cursor: not-allowed;
  }
`

/* ---------- Submitted status (replaces Save / Submit buttons) ---------- */
export const SubmittedStatusStyled = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  justify-content: center;
  color: var(--muted-2);
  font-size: 14px;
  .dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--brand-primary);
  }
  strong {
    color: var(--ink);
    font-weight: 600;
  }
`

/* ---------- State-licence rows ---------- */
export const LicenceListStyled = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`

export const LicenceRowStyled = styled.div`
  position: relative;
  background: var(--cream-base);
  border: 1px solid var(--border-2);
  border-radius: var(--radius-md);
  padding: 18px;
  .lic-row-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 14px;
  }
  .lic-row-title {
    font-size: 12px;
    font-weight: 600;
    color: var(--muted-2);
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }
  .lic-row-title b {
    color: var(--ink);
    text-transform: none;
    letter-spacing: 0;
    font-weight: 600;
    font-size: 13.5px;
    margin-left: 6px;
  }
  /* The dropzone sits on white inside the cream-tinted row so it stays visually distinct. */
  ${DropzoneStyled} {
    background: var(--surface);
  }
`

export const LicenceRemoveButtonStyled = styled.button`
  width: 30px;
  height: 30px;
  background: transparent;
  border: 1px solid transparent;
  border-radius: 6px;
  color: var(--muted);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background 120ms ease, color 120ms ease;
  flex-shrink: 0;
  &:hover {
    background: var(--danger-08);
    color: var(--danger);
  }
`

export const LicenceEmptyCopyStyled = styled.div`
  font-size: 13.5px;
  color: var(--muted-2);
  text-align: center;
  padding: 4px 0 8px;
`

export const LicenceAddButtonStyled = styled.button`
  margin-top: 14px;
  background: transparent;
  border: 1px solid var(--brand-primary-dark);
  color: var(--brand-primary-dark);
  border-radius: var(--radius-md);
  padding: 9px 16px;
  font-size: 14px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  transition: background 120ms ease, border-color 120ms ease;
  &:hover:not(:disabled) {
    background: var(--brand-primary-08);
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`
